import React, { useContext } from "react";
import { useState, useEffect, useCallback } from "react";
import {
  IonCol,
  IonGrid,
  IonPage,
  IonRow,
  IonIcon,
} from "@ionic/react";
import {
  IonItem,
  IonList,
  IonSelect,
  IonSelectOption,
  IonLabel,
} from "@ionic/react";
import { Route, Switch } from "react-router-dom";
import Patientdashboard from "../patientdashboard/patientdashboard";
import Datasync from "../datasync/datasync";
import Patientlog from "../patientlog/patientlog";
import Messages from "../messages/messages";
import RegisterDevice from "../../components/RegisterDevice";
import RegisterCharger from "../../components/RegisterCharger";
import RegisterPatient from "../../components/RegisterPatient";
import RegisterClinician from "../../components/RegisterClinician";
import RegisterSite from "../../components/RegisterSite";
import AssignDevice from "../../components/AssignDevice";
import Revoke from "../../components/Revoke";
import { IonImg } from "@ionic/react";
import calyanlogo from "../../assets/logos/calyanLogo.svg";
import { dashOptions } from "../../utils/jsonFiles/dashOptions";
import { useAppData } from "../../context/appContext";
import { useHistory } from "react-router-dom";
import { getUserInfo } from "../../service/adminUI";
import moment from "moment";
import { formatAppDate } from "../../utils/dateFormat";
import Report from "../report/report";
import "./dashboard.css";
import AppContext from "../../components/AppContext";
import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { unescapeCRFData } from "../../util/helpers/helperfunctions";
import { IMPLANT_DATE_OF_VISIT } from "../../util/config";
import { handleLogout } from "../../service/adminUI";
import AdminDashboard from "../../components/AdminDashboard";
import SessionManager from "../../service/SessionManager";
import ErrorMessage from "../../components/ErrorMessage";
import ErrorLoggingService from "../../service/ErrorLoggingService";
import HelpTour from "../../components/HelpTour";
import { dashboardTourSteps } from "../../utils/helpTours";
import Profile from "../profile/Profile";
import Settings from "../settings/Settings";
import { IoPersonCircleOutline } from "react-icons/io5";
import { settingsOutline } from "ionicons/icons";
import { getHistoricalData } from "../../util/helpers/HistoricalData";
import { initializeKeyboardShortcuts, ariaHelpers } from "../../utils/accessibility";
// DashboardLayoutManager is available for dashboard layout customization (SRS-WEB-068)
// import DashboardLayoutManager from "../../components/DashboardLayoutManager";


const selectedPatientSubject = new BehaviorSubject(null); //to emit patient selection event;

// Map to store historical symptoms for the selected patient
const patientSymptomsMap = new Map();
const symptomControlSubject = new BehaviorSubject(false); // Control symptom data fetch completion

const Dashboard = () => {
  const appContextData = useAppData();
  const history = useHistory();
  const [userInfo, setUserInfo] = useState();
  const [patientData, setPatientData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("Loading..Please wait");
  const [toastClass, setToastClass] = useState("toast-invisible");
  const [registerToastVisible, setRegisterToastVisible] = useState(false);
  // SRS-WEB-020: Badge counts for visual indicators
  // eslint-disable-next-line no-unused-vars
  const [unreadMessageCount, setUnreadMessageCount] = useState(0); // TODO: Implement message read/unread tracking
  const [unreadSymptomCount, setUnreadSymptomCount] = useState(0);
  const [lowBatteryCount, setLowBatteryCount] = useState(0);
  const {
    dtalService,
    userData,
    refreshTimerId,
    appData,
    setAppData,
    setRefreshTimerId,
  } = useContext(AppContext);
  const { userId } = userData;
  const isAdmin = userData.userType === "admin";
  
  // Get role display name (SRS-WEB-014)
  const getRoleDisplayName = () => {
    const roleMap = {
      'admin': 'Administrator',
      'clinician': 'Clinician',
    };
    return roleMap[userData.userType] || userData.userType;
  };

  const handleLogoutOnClick = async () => {
    try {
      const result = await handleLogout(
        userId,
        appData.refreshToken,
        appData.accessToken
      );
      if (result === 204) {
        //disable refresh token, clear context
        clearInterval(refreshTimerId.timerId);
        setRefreshTimerId({ timerId: null });
        setAppData({ count: 0, refreshToken: "", accessToken: "" });
        SessionManager.clearSession();
        history.push("/login");
      } else {
        setError(result);
        ErrorLoggingService.error("Logout failed", result, { userId, action: "logout" });
      }
    } catch (err) {
      setError(err);
      ErrorLoggingService.error("Logout error", err, { userId, action: "logout" });
    }
  };

  const handleBckImgChange = (name) => {
    // Background image change handler - kept for compatibility with child components
    // This function can be extended if needed
  };

  const handleSetRegisterToast = (toastParams) => {
    setToastClass(toastParams.css);
    setMessage(toastParams.message);
    setRegisterToastVisible(true);
    setTimeout(() => {
      setRegisterToastVisible(false);
    }, 5000);
  };

  // Helper function for retry delays
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const getPatients = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // 1 second base delay
    if (retryCount === 0) setPatientData(null);
    try {
      const devices = await dtalService.fetch("pacemaker", undefined, "thingId,attributes,features,_modified");
      const patients = devices.items;
      ErrorLoggingService.debug("Fetched patients", { count: patients.length });
      let filteredDevices = devices.items.filter((item) => {
        return item.attributes !== undefined;
      });
      // Site-based filtering: cross-reference with eCRF_subjects of the active site.
      // Pacemakers don't carry siteId; we resolve allowed subjectIds via the eCRF subject list.
      if (userData.activeSiteId) {
        const siteFilter = `eq(attributes/siteId,"${userData.activeSiteId}")`;
        const siteSubjects = await dtalService.fetch("eCRF_subjects", siteFilter, "attributes/patientId");
        const allowedSubjectIds = new Set(
          (siteSubjects?.items || [])
            .map(s => s?.attributes?.patientId)
            .filter(Boolean)
        );
        filteredDevices = filteredDevices.filter(d => allowedSubjectIds.has(d?.attributes?.subjectId));
        ErrorLoggingService.debug("Site-filtered devices", { siteId: userData.activeSiteId, count: filteredDevices.length });
      }
      ErrorLoggingService.debug("Filtered devices", { count: filteredDevices.length });
    await Promise.all(filteredDevices.map(async (device) => {
      const subjectId = device?.attributes.subjectId;
      const [syncTimerTimestamp, symptoms, implantDate] = await Promise.all([
        getSyncTimer(subjectId),
        getSymptoms(subjectId),
        getImplantDate(subjectId),
      ]);
      const { rriDate, eosDate } = getRriEosDates(device);

      // LAST SYNC: Use pacemaker _modified
      // device._modified reflects when the pacemaker was last interrogated/synced.
      const lastSyncDate = device?._modified
        ? formatAppDate(device._modified)
        : (syncTimerTimestamp !== undefined
          ? formatAppDate(moment.unix(syncTimerTimestamp))
          : "N/A");
      device["timestamp"] = lastSyncDate;

      const symptomsArray = Array.isArray(symptoms) ? symptoms : (symptoms ? [symptoms] : []);
      device["symptomCount"] = symptomsArray.length;
      device["implantDate"] = formatAppDate(implantDate);
      device["rriDate"] = rriDate;
      device["eosDate"] = eosDate;
    }));
    
    // Store patient data in React context (memory-only) - NO localStorage
    appContextData.setAllPatientData(filteredDevices);
    setPatientData(filteredDevices);
    setError(null); // Clear any previous errors on success
    } catch (err) {
      // Check if it's a 502 error and we haven't exceeded retry limit
      const is502Error = err?.response?.status === 502 || 
                       err?.message?.includes('502') || 
                       err?.status === 502 ||
                       (err?.message && err.message.includes('Bad Gateway'));
      
      if (is502Error && retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff
        ErrorLoggingService.warn(`502 error fetching patients, retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`, err);
        await sleep(delay);
        return getPatients(retryCount + 1);
      }
      
      ErrorLoggingService.error("Failed to fetch patients", err, { 
        component: "Dashboard", 
        action: "getPatients",
        retryCount 
      });
      // Ensure error is always a valid object, even if err is undefined
      const errorToSet = err || {
        message: 'Failed to fetch patients',
        status: 500,
        type: 'FETCH_ERROR'
      };
      setError(errorToSet);
      // Ensure patientData is always an array, even on error
      setPatientData([]);
      appContextData.setAllPatientData([]);
    }
  };

  const getSyncTimer = async (subjectId, from, to) => {
    const syncTimer = await dtalService.get(`syncTimer:${subjectId}`);
    return syncTimer[0]?.features.content.properties.triggerTimeStamp;
  };

  const getSymptoms = async (subjectId, from, to) => {
    // const symptoms = await DTALSERVICE.subscribe(`symptom:${subjectId}`)
    const symptoms = await dtalService.get(`symptom:${subjectId}`);
    return symptoms;
  };

  // Fetch historical symptoms for the selected patient
  const fetchHistoricalSymptoms = useCallback(async (subjectId) => {
    if (!subjectId) {
      setUnreadSymptomCount(0);
      return null;
    }

    try {
      // Clear previous symptoms
      patientSymptomsMap.clear();
      
      // Declare controlSubscription before subscription to avoid initialization error
      let controlSubscription = null;
      
      // Fetch historical symptoms using getHistoricalData (same as patientlog.jsx)
      const historicalSubscription = await getHistoricalData({
        dtalService,
        identifier: `symptom:${subjectId}`,
        thingUpdates: patientSymptomsMap,
        duControlSubject: symptomControlSubject
      });

      // Subscribe to symptom control subject to know when data is ready
      controlSubscription = symptomControlSubject.subscribe(isReady => {
        if (isReady) {
          // Update symptom count based on the Map size
          const symptomCount = patientSymptomsMap.size;
          setUnreadSymptomCount(symptomCount);
          
          // Unsubscribe from history and reset control
          dtalService.unsubscribe('history').then(() => {});
          symptomControlSubject.next(false);
          
          // Clean up subscription (historicalSubscription is an RxJS Subscription, not a Promise)
          if (historicalSubscription && typeof historicalSubscription.unsubscribe === 'function') {
            historicalSubscription.unsubscribe();
          }
          if (controlSubscription) {
            controlSubscription.unsubscribe();
          }
        }
      });

      // Return cleanup function
      return () => {
        if (controlSubscription) {
          controlSubscription.unsubscribe();
        }
        // historicalSubscription is an RxJS Subscription object, call unsubscribe directly
        if (historicalSubscription && typeof historicalSubscription.unsubscribe === 'function') {
          historicalSubscription.unsubscribe();
        }
      };
    } catch (err) {
      ErrorLoggingService.error("Failed to fetch historical symptoms", err, {
        component: "Dashboard",
        action: "fetchHistoricalSymptoms",
        subjectId
      });
      setUnreadSymptomCount(0);
      return null;
    }
  }, [dtalService]);

  const getImplantDate = async (subjectId) => {
    let implantDate = "";
    let eCRFData = await dtalService.get("eCRF_implant:" + subjectId);
    let implantData = eCRFData[0]?.features?.implant?.properties?.values;
    if (!_.isEmpty(implantData)) {
      implantData = unescapeCRFData(implantData);
      implantDate = implantData[IMPLANT_DATE_OF_VISIT]?.value ?? "";
    }
    return implantDate;
  };

  const getRriEosDates = (deviceData) => {
    try {
      const batteryDetails = deviceData?.features?.batteryparameter?.properties?.['Battery Life Details'];
      const daysToRri = batteryDetails?.['Days to RRI'];
      const daysToEos = batteryDetails?.['Days to EOS'];
      const modified = deviceData?._modified;

      if (!modified) return { rriDate: "", eosDate: "" };

      const baseDate = new Date(modified);
      if (isNaN(baseDate.getTime())) return { rriDate: "", eosDate: "" };

      const formatDate = (date) => formatAppDate(date);

      const addDays = (date, days) => {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
      };

      const rriDate = (daysToRri != null && daysToRri >= 0)
        ? formatDate(addDays(baseDate, daysToRri))
        : "";
      const eosDate = (daysToEos != null && daysToEos >= 0)
        ? formatDate(addDays(baseDate, daysToEos))
        : "";

      return { rriDate, eosDate };
    } catch (error) {
      ErrorLoggingService.error('Error calculating RRI/EOS dates', error, {
        component: "Dashboard"
      });
      return { rriDate: "", eosDate: "" };
    }
  };

  useEffect(() => {
    if(userData.userType === "clinician"){
      getPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.userType, userData.activeSiteId]);

  // SRS-WEB-020: Calculate badge counts for visual indicators
  // Fetch historical symptoms for the selected patient
  // Skip when on Data Sync or Messages - they use the same shared "history" EventSource;
  // fetching symptoms here would race with/override pacemaker sync log fetches.
  useEffect(() => {
    const selectedSubjectId = appContextData?.appUserData?.attributes?.subjectId || 
                             (patientData && patientData.length > 0 ? patientData[0]?.attributes?.subjectId : null);
    
    let cleanup = null;
    let cancelled = false;
    
    const currentPage = appContextData?.selected;
    if (currentPage === "Data Sync" || currentPage === "Messages") {
      return; // Skip - these pages use the shared history EventSource for their own data
    }
    
    if (selectedSubjectId) {
      fetchHistoricalSymptoms(selectedSubjectId).then(cleanupFn => {
        if (cancelled) {
          cleanupFn?.();
        } else {
          cleanup = cleanupFn;
        }
      });
    } else {
      setUnreadSymptomCount(0);
    }

    return () => {
      cancelled = true;
      if (cleanup) {
        cleanup();
      }
      dtalService?.unsubscribe('history').then(() => {});
    };
  }, [appContextData?.appUserData?.attributes?.subjectId, appContextData?.selected, patientData, fetchHistoricalSymptoms, dtalService]);

  // Calculate low battery count (for all patients)
  useEffect(() => {
    if (!patientData || patientData.length === 0) {
      setLowBatteryCount(0);
      return;
    }

    // Calculate low battery count
    // FIX #15: Battery SOC is at batteryparameter['Battery SOC (%)']
    let batteryCount = 0;
    patientData.forEach(patient => {
      const batteryLife = patient?.features?.batteryparameter?.properties?.['Battery Life Details']?.['Battery SOC (%)'];
      if (batteryLife !== undefined && batteryLife !== null && batteryLife < 20) { // Low battery threshold
        batteryCount++;
      }
    });
    setLowBatteryCount(batteryCount);
  }, [patientData]);

  const getuserInformation = async () => {
    // Use SessionManager for memory-only token access
    let token = SessionManager.getAccessToken();
    const userInfo = await getUserInfo(token);
    setUserInfo(userInfo);
  };

  useEffect(() => {
    getuserInformation();
  }, []);

  // SRS-WEB-076: Initialize keyboard shortcuts for accessibility
  useEffect(() => {
    const cleanup = initializeKeyboardShortcuts({
      dashboard: () => {
        appContextData.setSelected("Patient Dashboard");
        history.push("/dashboard/patientdashboard");
      },
      messages: () => {
        appContextData.setSelected("Messages");
        history.push("/dashboard/messages");
      },
      patients: () => {
        appContextData.setSelected("Patient Dashboard");
        history.push("/dashboard/patientdashboard");
      },
      settings: () => {
        appContextData.setSelected("Settings");
        history.push("/dashboard/settings");
      },
      help: () => {
        // Trigger help tour
        const helpEvent = new CustomEvent('show-help');
        window.dispatchEvent(helpEvent);
      }
    });

    return cleanup;
  }, [appContextData, history]);

  const handlePatientChange = (e) => {
    let updatedPatient = _.find(patientData, function (o) {
      return o.attributes.subjectId === e.target.value;
    });
    if (updatedPatient) {
      appContextData.setAppUserData(updatedPatient);
      selectedPatientSubject.next(updatedPatient.attributes.subjectId);
      ErrorLoggingService.info("Patient selected", {
        subjectId: updatedPatient.attributes.subjectId
      });
      // Only fetch symptoms on pages that aren't using their own historical SSE
      const currentPage = appContextData?.selected;
      if (currentPage !== "Data Sync" && currentPage !== "Messages") {
        fetchHistoricalSymptoms(updatedPatient.attributes.subjectId);
      }
    }
  };

  return (
    <IonPage style={{ background: "#E6E6E6" }}>
      <HelpTour steps={dashboardTourSteps} tourName="dashboard" />
      {error && (
        <ErrorMessage
          error={error}
          onDismiss={() => setError(null)}
          onRetry={() => {
            setError(null);
            if (userData.userType === "clinician") {
              getPatients();
            }
          }}
          autoHide={false}
        />
      )}
      <IonGrid>
        <IonRow>
          <IonCol
            className="dashboard_sidebar_color dashboard_sidebar_scroll"
            size="2.0"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingBottom: "2.75rem",
                paddingTop: "2rem",
              }}
            >
              <IonImg
                style={{ width: "10rem" }}
                src={calyanlogo}
                alt="calyan logo"
              ></IonImg>
              {/* SRS-WEB-014: Display user role and permissions */}
              <div style={{
                marginTop: "1rem",
                color: "white",
                fontSize: "12px",
                fontWeight: "bold"
              }}>
                {getRoleDisplayName()}
              </div>
            </div>
            {dashOptions
              .filter((el) => el.role.includes(userData.userType))
              .map((item, index) => {
                // SRS-WEB-020: Visual indicators (badge counts)
                let badgeCount = 0;
                if (item.title === "Messages") {
                  badgeCount = unreadMessageCount;
                } else if (item.title === "Patient Dashboard") {
                  badgeCount = lowBatteryCount;
                }
                
                return (
                  <div
                    data-testid={item.title}
                    key={index}
                    onClick={(e) => {
                      appContextData.setSelected(item.title);
                      history.push(`/dashboard/${item.route}`);
                    }}
                    onKeyDown={(e) => {
                      // SRS-WEB-076: Keyboard navigation support
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        appContextData.setSelected(item.title);
                        history.push(`/dashboard/${item.route}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={ariaHelpers.navLabel(item.title, badgeCount)}
                    aria-current={item.title === appContextData.selected ? 'page' : undefined}
                    className={
                      item.title === appContextData.selected
                        ? "selected"
                        : "unselected"
                    }
                    style={{ position: "relative", cursor: "pointer", outline: "none" }}
                  >
                    <div
                      className={
                        item.title === appContextData.selected
                          ? "logo_style_selected"
                          : "logo_style"
                      }
                    >
                      {item.logo}
                      {/* SRS-WEB-020: Badge count indicator */}
                      {badgeCount > 0 && (
                        <span 
                          aria-label={`${badgeCount} ${badgeCount === 1 ? 'item' : 'items'} requiring attention`}
                          role="status"
                          aria-live="polite"
                          style={{
                            position: "absolute",
                            top: "-5px",
                            right: "-5px",
                            backgroundColor: "#f44336",
                            color: "white",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            fontWeight: "bold"
                          }}
                        >
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </span>
                      )}
                    </div>
                    <div
                      className={
                        item.title === appContextData.selected
                          ? "dashboard_options_text_selected"
                          : "dashboard_options_text"
                      }
                    >
                      {item.title}
                    </div>
                  </div>
                );
              })}
            <br />

            <div
              onClick={(e) => {
                appContextData.setSelected("Profile");
                history.push("/dashboard/profile");
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  appContextData.setSelected("Profile");
                  history.push("/dashboard/profile");
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="User Profile"
              aria-current={appContextData.selected === "Profile" ? 'page' : undefined}
              className={
                appContextData.selected === "Profile"
                  ? "selected"
                  : "unselected"
              }
              style={{ cursor: "pointer", outline: "none" }}
            >
              <div
                className={
                  appContextData.selected === "Profile"
                    ? "logo_style_selected"
                    : "logo_style"
                }
              >
                <IoPersonCircleOutline />
              </div>
              <div
                className={
                  appContextData.selected === "Profile"
                    ? "dashboard_options_text_selected"
                    : "dashboard_options_text"
                }
              >
                Profile
              </div>
            </div>
            <div
              onClick={(e) => {
                appContextData.setSelected("Settings");
                history.push("/dashboard/settings");
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  appContextData.setSelected("Settings");
                  history.push("/dashboard/settings");
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Settings"
              aria-current={appContextData.selected === "Settings" ? 'page' : undefined}
              className={
                appContextData.selected === "Settings"
                  ? "selected"
                  : "unselected"
              }
              style={{ cursor: "pointer", outline: "none" }}
            >
              <div
                className={
                  appContextData.selected === "Settings"
                    ? "logo_style_selected"
                    : "logo_style"
                }
              >
                <IonIcon icon={settingsOutline} />
              </div>
              <div
                className={
                  appContextData.selected === "Settings"
                    ? "dashboard_options_text_selected"
                    : "dashboard_options_text"
                }
              >
                Settings
              </div>
            </div>
            <div
              className="unselected"
              onClick={handleLogoutOnClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleLogoutOnClick();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Logout"
              style={{ cursor: "pointer", outline: "none" }}
            >
              <div className="logo_style">
                <IonIcon
                  className="ham-item-icon"
                  src="../../assets/svg/logout.svg"
                />
              </div>
              <div className="dashboard_options_text">Logout</div>
            </div>
          </IonCol>
          <IonCol
            style={{ padding: "20px", height: "100vh" }}
            className=""
            size="10"
          >
            {/*
              Header row: Site ID + Hospital are shown on every clinician
              workspace page (Patient Dashboard, Data Sync, Symptoms Log,
              Messages) but hidden on Profile, Settings, and admin pages.
              The Subject dropdown is shown only on pages where a subject
              selection is meaningful (Data Sync, Symptoms Log, Messages) —
              Patient Dashboard itself is where patients get selected, so the
              dropdown there is redundant.
            */}
            {/*
              Header row: Site ID + Hospital are shown on every clinician
              workspace page (Patient Dashboard, Data Sync, Symptoms Log,
              Messages) but hidden on Profile, Settings, and admin pages.
              The Subject dropdown is shown only on pages where a subject
              selection is meaningful (Data Sync, Symptoms Log, Messages) —
              Patient Dashboard itself is where patients get selected, so the
              dropdown there is redundant.

              Style: no boxes.  Labels are the headings (bold), values sit
              directly under them, and a bottom border on the wrapper gives
              a single line separating the header strip from page content.
            */}
            {appContextData.selected !== "Profile" &&
              appContextData.selected !== "Settings" &&
              !isAdmin && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    paddingBottom: "16px",
                    marginBottom: "24px",
                    borderBottom: "1px solid #B0B0B0",
                    gap: "48px",
                    flexWrap: "wrap",
                  }}
                >
                  {userData.activeSiteId && (
                    <div data-testid="TSITEIDVAL">
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "#333",
                        }}
                      >
                        Site ID
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#333",
                          marginTop: "4px",
                        }}
                      >
                        {userData.activeSiteId}
                      </div>
                    </div>
                  )}
                  <div data-testid="THOSPITALVAL">
                    <div
                      style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#333",
                      }}
                    >
                      Hospital
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#333",
                        marginTop: "4px",
                      }}
                    >
                      {userInfo?.attributes?.hospital?.[0] || ""}
                    </div>
                  </div>

                  {patientData &&
                  appContextData.selected !== "Patient Dashboard" ? (
                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 700,
                          color: "#333",
                        }}
                      >
                        Subject
                      </div>
                      <select
                        data-testid="TPATIENTVAL"
                        value={
                          appContextData?.appUserData?.attributes?.subjectId ||
                          patientData[0]?.attributes?.subjectId ||
                          ""
                        }
                        onChange={handlePatientChange}
                        style={{
                          marginTop: "4px",
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#333",
                          background: "transparent",
                          border: "none",
                          outline: "none",
                          cursor: "pointer",
                          padding: "0",
                          minWidth: "140px",
                        }}
                      >
                        {patientData?.map((item, index) => (
                          <option
                            key={index}
                            value={item.attributes.subjectId}
                          >
                            {item.attributes.subjectId}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
              )}

            {/* {!isLoaded && <IonSpinner name="crescent"> </IonSpinner>} */}
            {(!patientData || !patientData.length) && (
              <IonLabel
                data-testid="TDASHBOARDMESSAGE"
                value={"No patients found"}
              >
                {" "}
              </IonLabel>
            )}
            {registerToastVisible && (
              <div className="toast-container">
                <div>
                  <div className={toastClass + " register-toast"}>
                    {" "}
                    {message}{" "}
                  </div>
                </div>
              </div>
            )}
            <Switch>
              <Route
                exact
                path="/dashboard/patientdashboard"
                render={() => <Patientdashboard patientData={patientData} symptomCount={unreadSymptomCount} selectedSubjectId={appContextData?.appUserData?.attributes?.subjectId} />}
              />
              <Route
                exact
                path="/dashboard/home"
                render={() => <AdminDashboard />}
              />
              <Route
                exact
                path="/dashboard/datasync"
                render={() => (
                  <Datasync selectedPatientSubject={selectedPatientSubject} />
                )}
              />
              <Route
                exact
                path="/dashboard/patientlog"
                render={() => (
                  <Patientlog selectedPatientSubject={selectedPatientSubject} />
                )}
              />
              <Route
                exact
                path="/dashboard/messages"
                render={() => <Messages />}
              />
              <Route exact path="/dashboard/profile" render={() => <Profile />} />
              <Route exact path="/dashboard/settings" render={() => <Settings />} />
              <Route exact path="/dashboard/report" render={() => <Report />} />
              <Route
                exact
                path="/dashboard/registerDevice"
                render={() => (
                  <RegisterDevice
                    text="Register Device"
                    bckimgChange={handleBckImgChange}
                    setRegisterToast={handleSetRegisterToast}
                  />
                )}
              />
              <Route
                exact
                path="/dashboard/registerPatient"
                render={() => (
                  <RegisterPatient
                    text="Register Patient"
                    bckimgChange={handleBckImgChange}
                    setRegisterToast={handleSetRegisterToast}
                  />
                )}
              />
              <Route
                exact
                path="/dashboard/registerClinician"
                render={() => (
                  <RegisterClinician
                    text="Register Clinician"
                    bckimgChange={handleBckImgChange}
                    setRegisterToast={handleSetRegisterToast}
                  />
                )}
              />
              <Route
                exact
                path="/dashboard/registerCharger"
                render={() => (
                  <RegisterCharger
                    text="Register Charger"
                    bckimgChange={handleBckImgChange}
                    setRegisterToast={handleSetRegisterToast}
                  />
                )}
              />
              <Route
                exact
                path="/dashboard/assignDevice"
                render={() => (
                  <AssignDevice
                    text="Assign Device"
                    bckimgChange={handleBckImgChange}
                  />
                )}
              />
              <Route
                exact
                path="/dashboard/registerSite"
                render={() => (
                  <RegisterSite
                    text="Register Site"
                    bckimgChange={handleBckImgChange}
                    setRegisterToast={handleSetRegisterToast}
                  />
                )}
              />
              <Route exact path="/dashboard/revoke">
                <Revoke text="Revoke" />
              </Route>
            </Switch>
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonPage>
  );
};

export default Dashboard;
